import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LoadingBar from '../components/LoadingBar';
import CountdownTimer from '../components/CountdownTimer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribeData, setSubscribeData] = useState({ email: '', name: '' });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [useTrial, setUseTrial] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('mercadopago'); // 'mercadopago' | 'pix'
  const [billingDay, setBillingDay] = useState(5);
  const [pixResult, setPixResult] = useState(null);
  const [pixPolling, setPixPolling] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  // Warm up the connection on mount so the very first POST to /api/auth/login
  // isn't the request that has to negotiate Cloudflare's __cf_bm cookie /
  // CORS preflight cache. This prevents the "click 2-3 times to login" bug.
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/api/public/pixel-config`, { credentials: 'include' })
      .catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Read directly from form inputs to handle Chrome autofill
      // (Chrome autofill doesn't always trigger React onChange)
      const form = e.target;
      const emailValue = form.querySelector('#login-email')?.value || loginData.email;
      const passwordValue = form.querySelector('#login-password')?.value || loginData.password;
      
      // Retry on transient network failures (Cloudflare bot challenge, cold start, etc.)
      // The first POST after a fresh page load can be aborted by Cloudflare bot
      // management while it sets the __cf_bm cookie. A short retry recovers seamlessly.
      const attemptLogin = async (attempts = 2) => {
        let lastError = null;
        for (let i = 0; i < attempts; i++) {
          try {
            return await axios.post(
              `${BACKEND_URL}/api/auth/login`,
              { email: emailValue, password: passwordValue },
              { withCredentials: true, timeout: 15000 }
            );
          } catch (err) {
            lastError = err;
            // Only retry on network-level failures (no HTTP response).
            // Do NOT retry on 4xx/5xx HTTP responses (e.g., wrong password).
            if (err.response) throw err;
            // brief backoff before retry
            await new Promise(r => setTimeout(r, 400));
          }
        }
        throw lastError;
      };

      const response = await attemptLogin();
      
      const userData = response.data.user;
      
      if (userData.role === 'admin') {
        toast.success('Bem-vindo, Administrador!');
        navigate('/admin');
        return;
      }
      
      if (userData.must_change_password) {
        toast.info('Por favor, altere sua senha no primeiro acesso.');
        navigate('/change-password');
        return;
      }
      
      // Overdue/cancelled users can still access the app with read-only access
      // The backend middleware will block mutation requests and show a popup
      if (userData.subscription_status === 'overdue' || userData.subscription_status === 'cancelled') {
        toast.warning('Sua assinatura está inativa. Algumas funcionalidades estão limitadas.');
      } else {
        toast.success('Login realizado com sucesso!');
      }
      navigate('/dashboard', { state: { user: userData } });
    } catch (error) {
      const serverDetail = error.response?.data?.detail;
      if (serverDetail) {
        toast.error(serverDetail);
      } else {
        // Network-level failure (CORS/preflight/Cloudflare challenge/timeout)
        toast.error('Não conseguimos conectar. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Digite seu email');
      return;
    }
    setSendingReset(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail.trim().toLowerCase() });
      toast.success('Se o email existir, enviaremos uma nova senha temporária.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Erro ao processar. Tente novamente.');
    } finally {
      setSendingReset(false);
    }
  };

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    setShowSubscribeModal(true);
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!selectedPlan) { toast.error('Selecione um plano'); return; }
    if (!subscribeData.email) { toast.error('Informe seu email'); return; }
    if (!acceptedTerms) { toast.error('Você precisa aceitar os Termos de Uso e Política de Privacidade'); return; }

    setSubscribing(true);
    const originUrl = window.location.origin;
    try {
      if (paymentMethod === 'pix') {
        const pixRes = await axios.post(`${BACKEND_URL}/api/mercadopago/pix/create`, {
          email: subscribeData.email,
          name: subscribeData.name,
          billing_day: billingDay,
          origin_url: originUrl,
        });
        setPixResult(pixRes.data);
        startPixPolling(pixRes.data.payment_id);
        return;
      }
      const checkoutRes = await axios.post(`${BACKEND_URL}/api/mercadopago/checkout`, {
        plan_id: selectedPlan,
        origin_url: originUrl,
        email: subscribeData.email,
        name: subscribeData.name,
        use_trial: useTrial,
      });
      if (!checkoutRes.data.url) throw new Error('URL de checkout não recebida');
      window.location.href = checkoutRes.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar cadastro. Tente novamente.');
    } finally {
      setSubscribing(false);
    }
  };

  const startPixPolling = (paymentId) => {
    setPixPolling(true);
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/mercadopago/pix/status/${paymentId}`);
        if (res.data.status === 'approved') {
          clearInterval(interval);
          setPixPolling(false);
          toast.success('Pagamento aprovado! Verifique seu email para acessar a plataforma.');
          setPixResult(null);
          setShowSubscribeModal(false);
          navigate('/login');
        } else if (res.data.status === 'rejected' || res.data.status === 'cancelled') {
          clearInterval(interval);
          setPixPolling(false);
          toast.error('Pagamento não aprovado. Tente novamente.');
        }
      } catch {
        // Silently retry
      }
    }, 5000);
    // Stop polling after 30 min
    setTimeout(() => { clearInterval(interval); setPixPolling(false); }, 1800000);
  };

  const copyPixCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código PIX copiado!');
  };

  const plans = [
    { id: 'unico', name: 'Plano Aretê', price: 'R$ 9,90', oldPrice: 'R$ 29,90', period: '/mês', trialDays: 7, style: 'bg-green-50 border-2 border-green-500 hover:bg-green-100', textColor: 'text-green-700' }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen w-full" style={{ background: 'var(--neo-bg)' }}>
      {loading && <LoadingBar text="Entrando..." />}
      {subscribing && <LoadingBar text="Processando pagamento..." />}
      {/* Left Panel - Auth Form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <img 
              src="/logo-arete.png" 
              alt="Aretê" 
              className="h-20 mx-auto mb-4 object-contain drop-shadow-lg"
            />
            <p className="text-[var(--neo-text-light)]">Controle suas finanças de forma inteligente</p>
          </div>

          <div className="neo-card">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-[var(--neo-text-dark)]">Bem-vindo de volta</h2>
              <p className="text-sm text-[var(--neo-text-light)]">Entre com sua conta para continuar</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[var(--neo-text)]" htmlFor="login-email">Email</label>
                <input
                  id="login-email"
                  data-testid="login-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  className="neo-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-[var(--neo-text)]" htmlFor="login-password">Senha</label>
                <div className="relative">
                  <input
                    id="login-password"
                    data-testid="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="neo-input w-full pr-10"
                  />
                  <button
                    type="button"
                    data-testid="login-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--neo-text-light)] hover:text-[var(--neo-text)] transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <button type="submit" className="neo-btn w-full" disabled={loading} data-testid="login-submit">
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
              <div className="text-center mt-4">
                <button 
                  type="button" 
                  className="text-sm text-[var(--neo-green)] hover:underline" 
                  onClick={() => setShowForgotPassword(true)}
                  data-testid="forgot-password-button"
                >
                  Esqueci minha senha
                </button>
              </div>
            </form>
            
            <div className="neo-divider my-6" />
            
            <div className="neo-pressed p-5 rounded-xl">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-2xl">🚀</span>
                <p className="text-lg font-bold text-[var(--neo-green)]">Novo por aqui?</p>
              </div>
              <p className="text-sm text-center text-[var(--neo-text-light)] mb-4">
                Descubra como a Aretê pode transformar suas finanças com IA e praticidade!
              </p>
              <button 
                className="neo-btn w-full mb-3"
                onClick={() => navigate('/')}
              >
                ✨ Conhecer a Aretê →
              </button>
              <button 
                className="neo-btn-outline w-full"
                onClick={() => {
                  setSelectedPlan(null);
                  setShowSubscribeModal(true);
                }}
              >
                Já quero assinar!
              </button>
              <div className="flex items-center justify-center gap-1 mt-3">
                <div className="flex -space-x-2">
                  <img src="https://images.unsplash.com/photo-1629425733761-caae3b5f2e50?w=32&h=32&fit=crop&crop=face" alt="" className="w-6 h-6 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=32&h=32&fit=crop&crop=face" alt="" className="w-6 h-6 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1611432579402-7037e3e2c1e4?w=32&h=32&fit=crop&crop=face" alt="" className="w-6 h-6 rounded-full border-2 border-white object-cover" />
                </div>
                <p className="text-xs text-[var(--neo-text-light)] ml-1">+200 pessoas já usam</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-col justify-center p-12 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--neo-bg-light) 0%, var(--neo-bg) 100%)' }}>
        <div className="neo-card">
          <h2 className="text-4xl font-bold tracking-tight leading-tight text-[var(--neo-text-dark)] mb-4">
            Controle total das suas <span className="text-gradient-green">finanças</span>
          </h2>
          <p className="text-lg text-[var(--neo-text-light)] max-w-lg mb-8">
            Organize suas receitas, despesas, metas e investimentos em um só lugar.
          </p>
          <div className="grid gap-4">
            <div className="neo-stat-card flex items-start space-x-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--neo-green)', color: 'white' }}>
                <span className="font-bold text-lg">✓</span>
              </div>
              <div>
                <h3 className="font-semibold text-[var(--neo-text-dark)] mb-1">Dashboard completo</h3>
                <p className="text-sm text-[var(--neo-text-light)]">Visualize suas finanças em gráficos intuitivos</p>
              </div>
            </div>
            <div className="neo-stat-card flex items-start space-x-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--neo-gold)', color: 'white' }}>
                <span className="font-bold text-lg">✓</span>
              </div>
              <div>
                <h3 className="font-semibold text-[var(--neo-text-dark)] mb-1">Metas financeiras</h3>
                <p className="text-sm text-[var(--neo-text-light)]">Defina e acompanhe seus objetivos</p>
              </div>
            </div>
            <div className="neo-stat-card flex items-start space-x-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--neo-green-dark)', color: 'white' }}>
                <span className="font-bold text-lg">✓</span>
              </div>
              <div>
                <h3 className="font-semibold text-[var(--neo-text-dark)] mb-1">Projeções inteligentes</h3>
                <p className="text-sm text-[var(--neo-text-light)]">Veja como suas finanças evoluirão</p>
              </div>
            </div>
          </div>
          
          {/* Subscription Plans */}
          <div className="mt-8 neo-pressed p-6 rounded-xl">
            <h3 className="font-semibold text-[var(--neo-text-dark)] mb-4">Planos Disponíveis</h3>
            <div className="mb-4 flex justify-center" data-testid="login-side-countdown">
              <CountdownTimer compact />
            </div>
            <div className="grid grid-cols-1 gap-3">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => handleSelectPlan(plan.id)}
                  className="neo-raised text-center p-6 rounded-xl cursor-pointer transition-all duration-200 hover:scale-[1.02] border-2 border-[var(--neo-green)]"
                >
                  <div className="inline-flex items-center gap-1.5 mb-2 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider">
                    <span className="inline-block w-1 h-1 rounded-full bg-rose-600 animate-pulse" />
                    67% OFF • Oferta de lançamento
                  </div>
                  <p className="font-semibold text-[var(--neo-text-dark)]">{plan.name}</p>
                  <p className="text-sm text-gray-400 line-through decoration-rose-500 mt-1">{plan.oldPrice}</p>
                  <p className="font-bold text-2xl text-[var(--neo-green)]">{plan.price}<span className="text-sm font-normal text-[var(--neo-text-light)]">{plan.period}</span></p>
                  <p className="text-xs text-[var(--neo-gold)] mt-2 font-medium">7 dias grátis • Clique para assinar</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Subscribe Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg shadow-2xl animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-xl">
                {pixResult ? 'Pagamento PIX' : selectedPlan ? 'Assine o Plano Aretê' : 'Escolha seu Plano'}
              </CardTitle>
              <CardDescription>
                {pixResult 
                  ? 'Escaneie o QR Code ou copie o código para pagar'
                  : selectedPlan 
                    ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="line-through text-gray-400">R$ 29,90</span>
                          <span className="font-bold text-emerald-600">R$ 9,90/mês</span>
                          <span className="text-xs">— Acesso completo</span>
                        </span>
                      )
                    : 'Selecione um plano para começar'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!pixResult && (
                <div className="mb-4 flex justify-center" data-testid="login-modal-countdown">
                  <CountdownTimer compact />
                </div>
              )}
              {pixResult ? (
                <div className="space-y-4" data-testid="pix-result-section">
                  <div className="text-center p-4 bg-gray-50 rounded-xl border">
                    <p className="text-2xl font-bold text-gray-900 mb-1">R$ {pixResult.amount?.toFixed(2).replace('.', ',')}</p>
                    <p className="text-xs text-gray-500">Vencimento dia {billingDay} de cada mês</p>
                  </div>
                  {pixResult.qr_code_base64 && (
                    <div className="flex justify-center">
                      <img 
                        src={`data:image/png;base64,${pixResult.qr_code_base64}`} 
                        alt="QR Code PIX" 
                        className="w-48 h-48 rounded-lg border"
                        data-testid="pix-qr-code-img"
                      />
                    </div>
                  )}
                  {pixResult.qr_code && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">Código PIX (copia e cola):</p>
                      <div className="relative">
                        <div className="bg-gray-100 rounded-lg p-3 pr-20 text-xs font-mono break-all text-gray-700 max-h-20 overflow-y-auto" data-testid="pix-code-text">
                          {pixResult.qr_code}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          className="absolute top-2 right-2 text-xs"
                          onClick={() => copyPixCode(pixResult.qr_code)}
                          data-testid="pix-copy-btn"
                        >
                          Copiar
                        </Button>
                      </div>
                    </div>
                  )}
                  {pixPolling && (
                    <div className="flex items-center justify-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm text-amber-700">Aguardando confirmação do pagamento...</p>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 text-center">
                    Após o pagamento, você receberá um email com os dados de acesso. 
                    Todo mês, 3 dias antes do vencimento, enviaremos o código PIX por email e WhatsApp.
                  </p>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full"
                    onClick={() => { setPixResult(null); setShowSubscribeModal(false); setSelectedPlan(null); setSubscribeData({ email: '', name: '' }); setAcceptedTerms(false); setPaymentMethod('mercadopago'); }}
                    data-testid="pix-close-btn"
                  >
                    Fechar
                  </Button>
                </div>
              ) : !selectedPlan ? (
                <div className="space-y-3">
                  {plans.map((plan) => (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className="w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md border-green-500 bg-green-50 hover:bg-green-100"
                    >
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{plan.name}</p>
                          <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">7 dias grátis</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{plan.period} após trial</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400 line-through decoration-rose-500">{plan.oldPrice}</p>
                        <p className={`font-bold text-xl ${plan.textColor}`}>{plan.price}</p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <form onSubmit={handleCheckout} className="space-y-4">
                  {/* Payment option selector */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Como deseja pagar?</p>
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        type="button"
                        data-testid="option-pay-now"
                        onClick={() => { setPaymentMethod('mercadopago'); setUseTrial(false); }}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                          paymentMethod === 'mercadopago' && !useTrial
                            ? 'border-blue-500 bg-blue-50 shadow-sm' 
                            : 'border-muted hover:border-blue-300 bg-background'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${paymentMethod === 'mercadopago' && !useTrial ? 'border-blue-500' : 'border-muted-foreground'}`}>
                          {paymentMethod === 'mercadopago' && !useTrial && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-blue-800">Pagar agora — <span className="line-through text-gray-400 font-normal">R$ 29,90</span> <span className="text-emerald-600">R$ 9,90</span>/mês</p>
                          <p className="text-xs text-muted-foreground">Cartão de crédito ou débito. Acesso imediato.</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        data-testid="option-pix"
                        onClick={() => { setPaymentMethod('pix'); setUseTrial(false); }}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                          paymentMethod === 'pix'
                            ? 'border-teal-500 bg-teal-50 shadow-sm' 
                            : 'border-muted hover:border-teal-300 bg-background'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${paymentMethod === 'pix' ? 'border-teal-500' : 'border-muted-foreground'}`}>
                          {paymentMethod === 'pix' && <div className="w-2 h-2 rounded-full bg-teal-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-teal-800">PIX Agendado — <span className="line-through text-gray-400 font-normal">R$ 29,90</span> <span className="text-emerald-600">R$ 9,90</span>/mês</p>
                          <p className="text-xs text-muted-foreground">Pague via PIX. Escolha o dia do vencimento.</p>
                        </div>
                      </button>
                      <button
                        type="button"
                        data-testid="option-trial"
                        onClick={() => { setPaymentMethod('mercadopago'); setUseTrial(true); }}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                          useTrial 
                            ? 'border-green-500 bg-green-50 shadow-sm' 
                            : 'border-muted hover:border-green-300 bg-background'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${useTrial ? 'border-green-500' : 'border-muted-foreground'}`}>
                          {useTrial && <div className="w-2 h-2 rounded-full bg-green-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-green-800">7 dias grátis</p>
                          <p className="text-xs text-muted-foreground">Teste sem compromisso. Cartão de crédito necessário.</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* PIX billing day selector */}
                  {paymentMethod === 'pix' && (
                    <div className="space-y-2" data-testid="pix-billing-day-section">
                      <Label>Dia de vencimento mensal</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[5, 15, 30].map((day) => (
                          <button
                            key={day}
                            type="button"
                            data-testid={`billing-day-${day}`}
                            onClick={() => setBillingDay(day)}
                            className={`p-3 rounded-lg border-2 text-center transition-all ${
                              billingDay === day
                                ? 'border-teal-500 bg-teal-50 shadow-sm'
                                : 'border-muted hover:border-teal-300'
                            }`}
                          >
                            <p className={`font-bold text-lg ${billingDay === day ? 'text-teal-700' : 'text-gray-700'}`}>Dia {day}</p>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Você receberá o código PIX por email e WhatsApp 3 dias antes do vencimento.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="subscribe-name">Nome completo</Label>
                    <Input
                      id="subscribe-name"
                      type="text"
                      placeholder="Seu nome"
                      value={subscribeData.name}
                      onChange={(e) => setSubscribeData({ ...subscribeData, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subscribe-email">Email *</Label>
                    <Input
                      id="subscribe-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={subscribeData.email}
                      onChange={(e) => setSubscribeData({ ...subscribeData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="flex items-start gap-2 p-3 rounded-lg border border-muted">
                    <input
                      type="checkbox"
                      id="login-accept-terms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      data-testid="login-accept-terms-checkbox"
                    />
                    <label htmlFor="login-accept-terms" className="text-xs text-muted-foreground cursor-pointer select-none">
                      Li e aceito os{' '}
                      <a href="/termos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">Termos de Uso</a>
                      {' '}e a{' '}
                      <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">Política de Privacidade</a>
                      {' '}da Aretê. *
                    </label>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <p className="text-xs text-blue-700">
                      {paymentMethod === 'pix' 
                        ? `Pagamento seguro via PIX (Mercado Pago). Vencimento todo dia ${billingDay}. Você receberá um email para definir sua senha.`
                        : useTrial 
                          ? 'Pagamento seguro via Mercado Pago. Cobrança apenas após 7 dias. Você receberá um email para definir sua senha.'
                          : 'Pagamento seguro via Mercado Pago. PIX, crédito ou débito. Você receberá um email para definir sua senha.'}
                    </p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        setShowSubscribeModal(false);
                        setSelectedPlan(null);
                        setSubscribeData({ email: '', name: '' });
                        setAcceptedTerms(false);
                        setPaymentMethod('mercadopago');
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      type="submit" 
                      className={`flex-1 ${paymentMethod === 'pix' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-green-600 hover:bg-green-700'}`}
                      disabled={subscribing || !acceptedTerms}
                      data-testid="subscribe-submit-btn"
                    >
                      {subscribing ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Processando...
                        </span>
                      ) : paymentMethod === 'pix' ? (
                        'Gerar PIX'
                      ) : useTrial ? (
                        'Começar Teste Grátis'
                      ) : (
                        'Pagar e Começar Agora'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowForgotPassword(false)}>
          <Card className="w-full max-w-md neo-card" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-xl">Recuperar Senha</CardTitle>
              <CardDescription>Enviaremos uma nova senha temporária para seu email</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email">Seu email de cadastro</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="email@exemplo.com"
                    required
                    className="neo-input mt-1"
                    data-testid="forgot-email-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowForgotPassword(false); setForgotEmail(''); }}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1" disabled={sendingReset} data-testid="forgot-submit-btn">
                    {sendingReset ? 'Enviando...' : 'Enviar Nova Senha'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Login;
